import React, { createContext, useContext, useState, useCallback } from 'react';

/**
 * Prototype-only context for the active device preview.
 *
 * Reviewers can toggle between mobile, tablet, and desktop to see how the
 * employee flow renders at each form factor. The choice persists to
 * `sessionStorage` so page refreshes survive, but not across tabs.
 */

const STORAGE_KEY = 'cyf-prototype-device';
const VALID = ['mobile', 'tablet', 'desktop'];

const readInitial = () => {
  try {
    const stored = typeof window !== 'undefined' ? window.sessionStorage.getItem(STORAGE_KEY) : null;
    return VALID.includes(stored) ? stored : 'mobile';
  } catch (e) {
    return 'mobile';
  }
};

const DeviceContext = createContext(null);

export const DeviceProvider = ({ children }) => {
  const [device, setDeviceState] = useState(readInitial);

  const setDevice = useCallback((next) => {
    if (!VALID.includes(next)) return;
    setDeviceState(next);
    try {
      window.sessionStorage.setItem(STORAGE_KEY, next);
    } catch (e) {
      /* ignore */
    }
  }, []);

  return <DeviceContext.Provider value={{ device, setDevice }}>{children}</DeviceContext.Provider>;
};

export const useDevice = () => {
  const ctx = useContext(DeviceContext);
  if (!ctx) return { device: 'mobile', setDevice: () => {} };
  return ctx;
};

export const DEVICE_OPTIONS = VALID;
